import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import { MissingManifestResponse } from "../../app/models/responseModels/missingManifestResponse";
import { toast } from "react-toastify";
import {
  downloadMissingManifestDataToCsv,
  downloadMissingManifestDataToXlsx,
  getMissingManifestList,
} from "../../app/features/missingManifest/missingManifestSlice";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import {
  MISSING_MANIFEST_COLUMN_NAME,
  MISSING_MANIFEST_LABEL_NAME,
} from "./MissingmanifestConstants";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import MissingManifestDataFilter from "./MissingManifestDataFilter";

const breadcrumb = ["Missing Manifest"];

const MissingManifest = () => {
  const dispatch = useAppDispatch();
  const { isLoading, missingManifestList } = useSelector(
    (state: RootState) => state.missingManifest
  );

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [filteredData, setFilteredData] = useState<MissingManifestResponse[]>(
    []
  );

  // ***** Callbacks *****
  const getMissingManifestData = useCallback(async () => {
    try {
      const response = await dispatch(getMissingManifestList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "missingManifestList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "missingManifestListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getMissingManifestData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getMissingManifestData]);

  useEffect(() => {
    if (missingManifestList.length > 0) {
      if (filteredData.length === missingManifestList.length) {
        setFilteredData(missingManifestList);
      }
    } else {
      setFilteredData([]);
    }
  }, [missingManifestList, filteredData]);

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<MissingManifestResponse>[]>(
    () => [
      {
        accessorKey: MISSING_MANIFEST_COLUMN_NAME.LOCATION_NAME,
        header: MISSING_MANIFEST_LABEL_NAME.LOCATION_NAME,
        size: 100,
      },
      {
        accessorKey: MISSING_MANIFEST_COLUMN_NAME.TRACKING_NUMBER,
        header: MISSING_MANIFEST_LABEL_NAME.TRACKING_NUMBER,
        size: 200,
      },
      {
        accessorKey: MISSING_MANIFEST_COLUMN_NAME.ARRIVAL_DATE,
        header: MISSING_MANIFEST_LABEL_NAME.ARRIVAL_DATE,
        size: 80,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.arrivalDate ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleDateString()}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: MISSING_MANIFEST_COLUMN_NAME.NOTES,
        header: MISSING_MANIFEST_LABEL_NAME.NOTES,
        size: 200,
      },
      {
        accessorKey: MISSING_MANIFEST_COLUMN_NAME.CREATED_ON,
        header: MISSING_MANIFEST_LABEL_NAME.CREATED_ON,
        size: 80,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.createdOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleDateString()}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: MISSING_MANIFEST_COLUMN_NAME.UPDATED_ON,
        header: MISSING_MANIFEST_LABEL_NAME.UPDATED_ON,
        size: 80,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.updatedOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleDateString()}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
    ],
    []
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getMissingManifestData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadMissingManifestDataToCsv(filteredData)
      ).unwrap();

      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "missingManifestDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "missingManifestDownloadCsvException",
      });
    }
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadMissingManifestDataToXlsx(filteredData)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "MissingManifest.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "missingManifestDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "missingManifestDownloadXlsxException",
      });
    }
  };

  // ***** Datagrid Custom Actions *****
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<MissingManifestResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "blue" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <MissingManifestDataFilter
          missingManifestData={missingManifestList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No missing manifest"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={true}
        defaultSortColumnName="arrivalDate"
      />
    </Box>
  );
};

export default MissingManifest;
